"use client";

import React from "react";
import styles from "./infrastructure-status.module.scss";
import { HealthResponse } from "@/types/health.types";
import ServiceRowBadge from "../components/service-row-badge/service-row-badge";
import CountBadge from "../components/count-badge/count-badge";
import { formatBalance, formatEthBalance } from "../utils/format-utils";
import { getChainName, getChainLogo } from "../utils/chain-utils";
import { createScrollHandler } from "../utils/scroll-utils";

interface InfrastructureStatusProps {
  healthData: HealthResponse;
}

const InfrastructureStatus: React.FC<InfrastructureStatusProps> = ({ healthData }) => {


  const getBalanceCounts = () => {
    const balances: { healthy: boolean }[] = [];

    if (healthData.delivery?.available) {
      balances.push(
        { healthy: healthData.delivery.blessnetHealthy },
        { healthy: healthData.delivery.parentChainHealthy }
      );
    }

    if (healthData.dispenser?.available) {
      balances.push({ healthy: healthData.dispenser.healthy });
    }

    if (healthData.sprinkler?.available) {
      balances.push({ healthy: healthData.sprinkler.healthy });
    }

    if (healthData.blessRouter?.available) {
      balances.push({ healthy: healthData.blessRouter.healthy });
    }

    const healthyCount = balances.filter(b => b.healthy).length;
    const unhealthyCount = balances.filter(b => !b.healthy).length;

    return { healthyCount, unhealthyCount, totalCount: balances.length };
  };

  const scrollToInfrastructureContent = createScrollHandler('infrastructure-content');

  return (
    <div id="infrastructure-status" className={styles.card}>
      <div className={styles.header}>
        <h3>Infra Balances</h3>
        <div className={styles.summary}>
          {getBalanceCounts().healthyCount > 0 && (
            <CountBadge
              count={getBalanceCounts().healthyCount}
              label="Healthy"
              isHealthy={true}
              onClick={scrollToInfrastructureContent}
            />
          )}
          {getBalanceCounts().unhealthyCount > 0 && (
            <CountBadge
              count={getBalanceCounts().unhealthyCount}
              label="Unhealthy"
              isHealthy={false}
              onClick={scrollToInfrastructureContent}
            />
          )}
        </div>
      </div>

      <div id="infrastructure-content" className={styles.content}>
        <div className={styles.balanceGrid}>
          {/* delivery service */}
          {healthData.delivery?.available && (
            <>
              <ServiceRowBadge
                isHealthy={healthData.delivery.blessnetHealthy}
                serviceName="delivery service"
                chainId={healthData.delivery.blessnetChainId}
                metrics={[
                  {
                    label: "BLESS",
                    value: formatBalance(healthData.delivery.blessnetBalance)
                  }
                ]}
              />
              <ServiceRowBadge
                isHealthy={healthData.delivery.parentChainHealthy}
                serviceName="delivery service"
                chainId={healthData.delivery.parentChainId}
                metrics={[
                  {
                    label: "ETH",
                    value: formatEthBalance(healthData.delivery.parentChainBalance)
                  }
                ]}
              />
            </>
          )}

          {/* token dispenser */}
          {healthData.dispenser?.available && (
            <ServiceRowBadge
              isHealthy={healthData.dispenser.healthy}
              serviceName="token dispenser"
              chainId={healthData.dispenser.blessnetChainId}
              metrics={[
                {
                  label: "BLESS",
                  value: formatBalance(healthData.dispenser.balance)
                }
              ]}
            />
          )}

          {/* sprinkler service */}
          {healthData.sprinkler?.available && (
            <ServiceRowBadge
              isHealthy={healthData.sprinkler.healthy}
              serviceName="sprinkler service"
              chainId={healthData.sprinkler.blessnetChainId}
              metrics={[
                {
                  label: "BLESS",
                  value: formatBalance(healthData.sprinkler.balance)
                }
              ]}
            />
          )}

          {/* bless router */}
          {healthData.blessRouter?.available && (
            <ServiceRowBadge
              isHealthy={healthData.blessRouter.healthy}
              serviceName="bless router"
              chainId={healthData.blessRouter.parentChainId}
              metrics={[
                {
                  label: "ETH",
                  value: formatEthBalance(healthData.blessRouter.balance)
                }
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InfrastructureStatus;
